// TipTap-based rich-text editor for composing marketing emails. Drop-in
// replacement for a <textarea>: value/onChange are HTML strings.
//
// Featured: bold / italic / underline (via strike for now since TipTap
// starter-kit doesn't ship underline standalone), H1/H2, bullet + ordered
// lists, blockquote, horizontal rule, links, undo/redo, and — the
// load-bearing feature — an "Insert variable" dropdown that drops
// {{firstName}}-style tokens as visual chips via TipTap's Mention
// extension. The chip is atomic (one delete keystroke removes the whole
// brace pair, no half-broken state); on serialize-to-HTML it becomes a
// literal `{{firstName}}` token that the server's renderTemplate()
// substitutes per recipient at send time.
//
// Props:
//   value:            string (HTML)
//   onChange(html):   fired on every keystroke
//   placeholder:      string
//   variables:        Array of { id, label, scope } from
//                     admin/email-variable-catalog. Drives the menu.
//   minHeight:        css value (default 240px)

import {
  EditorContent,
  useEditor,
  ReactNodeViewRenderer,
  NodeViewWrapper,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import { useEffect, useRef, useState } from 'react';

// Atomic visual chip for `{{varName}}`. Looks like a pill, deletes in one
// keystroke, gets serialized back to literal {{varName}} via renderHTML.
function VariableChipView({ node }) {
  const id = node.attrs?.id || '';
  return (
    <NodeViewWrapper
      as="span"
      data-type="variable"
      data-id={id}
      className="inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full bg-indigo-100 text-indigo-800 text-sm font-medium select-none"
      contentEditable={false}
    >
      {`{{${id}}}`}
    </NodeViewWrapper>
  );
}

// Configure Mention as a "variable" type. We DON'T use TipTap's typeahead
// suggestion popup — the variable list is small and surfaces better via
// a top-level toolbar dropdown.
const VariableExtension = Mention.extend({
  name: 'variable',
  renderHTML({ node }) {
    return ['span', {
      'data-type': 'variable',
      'data-id': node.attrs.id,
      class: 'email-variable-chip',
    }, `{{${node.attrs.id}}}`];
  },
  // Parse `<span data-type="variable" data-id="firstName">` back into a node
  // on the way in (so editing an existing template's stored HTML works).
  parseHTML() {
    return [{
      tag: 'span[data-type="variable"]',
      getAttrs: (el) => ({ id: el.getAttribute('data-id') }),
    }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VariableChipView);
  },
}).configure({
  // We're not using suggestion — disable the autocomplete popup wiring.
  suggestion: { char: ' ', allowSpaces: false, items: () => [] },
});

function ToolbarButton({ active, disabled, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); }}
      onClick={onClick}
      disabled={disabled}
      className={`px-2 py-1 text-sm rounded ${
        active ? 'bg-gray-200 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function VariableMenu({ editor, variables }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const insert = (id) => {
    editor.chain().focus().insertContent({
      type: 'variable',
      attrs: { id, label: id },
    }).run();
    setOpen(false);
  };

  if (!variables || variables.length === 0) return null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        className="px-2 py-1 text-sm rounded text-indigo-700 hover:bg-indigo-50 font-medium"
        title="Insert a variable that the server replaces per recipient on send"
      >
        + Variable
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white shadow-lg rounded-md border border-gray-200 py-1 max-h-72 overflow-auto">
          {variables.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => insert(v.id)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{v.label}</span>
                <span className="text-xs text-gray-400">{v.scope}</span>
              </div>
              <div className="text-xs text-gray-500 font-mono">{`{{${v.id}}}`}</div>
              {v.example && <div className="text-xs text-gray-400 mt-0.5">e.g. {v.example}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailEditor({
  value = '',
  onChange,
  placeholder = 'Write your message…',
  variables = [],
  minHeight = '240px',
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      VariableExtension,
    ],
    content: value || '',
    onUpdate({ editor: e }) {
      onChange?.(e.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none px-3 py-3',
        'data-placeholder': placeholder,
      },
    },
  });

  // Keep content in sync when the parent resets value (e.g. "Start from
  // template" picker pre-fills body). Only set when the incoming value
  // differs from the editor's current HTML — avoids cursor reset on
  // every keystroke.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="border border-gray-300 rounded-md bg-gray-50" style={{ minHeight }} />;
  }

  const promptLink = () => {
    const prev = editor.getAttributes('link').href || '';
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border border-gray-300 rounded-md bg-white">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-gray-200 bg-gray-50 rounded-t-md">
        <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)">
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)">
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
          <s>S</s>
        </ToolbarButton>
        <span className="mx-1 text-gray-300">|</span>
        <ToolbarButton active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
          H1
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
          H2
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()} title="Paragraph">
          ¶
        </ToolbarButton>
        <span className="mx-1 text-gray-300">|</span>
        <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bulleted list">
          • List
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
          1. List
        </ToolbarButton>
        <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote">
          “”
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
          ―
        </ToolbarButton>
        <span className="mx-1 text-gray-300">|</span>
        <ToolbarButton active={editor.isActive('link')} onClick={promptLink} title="Link">
          🔗
        </ToolbarButton>
        <span className="mx-1 text-gray-300">|</span>
        <VariableMenu editor={editor} variables={variables} />
        <span className="flex-1" />
        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
          ↶
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
          ↷
        </ToolbarButton>
      </div>
      <div style={{ minHeight }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
